// src/components/client/Projects.jsx

import React, { useState, useEffect, useMemo, useContext } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import ProjectService from "../../../services/ProjectService";
import { FaPlus, FaEdit, FaTrash, FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { 
  ChevronLeft, 
  Edit, 
  Trash, 
  Search, 
  Filter, 
  RefreshCw, 
  Download,
  Calendar,
  Users,
  Building,
  FileText,
  Activity
} from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";

const Projects = () => {
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const navigate = useNavigate();
  const { user, getProjects, projects, setProjects, projectLoading } = useContext(AuthContext);

  // Filters state
  const [filters, setFilters] = useState({
    status: "ALL",
    client: "ALL",
    searchTerm: "",
    dateFrom: "",
    dateTo: "",
    minUpdates: "",
    maxUpdates: "",
    minTickets: "",
    maxTickets: "",
  });

  const [filteredProjects, setFilteredProjects] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Filter projects based on filter criteria
  useEffect(() => {
    if (!projects || projects.length === 0) {
      setFilteredProjects([]);
      return;
    }

    let filtered = [...projects];

    // Status filter
    if (filters.status !== "ALL") {
      filtered = filtered.filter(project => project.status === filters.status);
    }

    // Client filter
    if (filters.client !== "ALL") {
      filtered = filtered.filter(project => project.client?._id === filters.client);
    }

    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(project => 
        project.name?.toLowerCase().includes(searchLower) ||
        project.description?.toLowerCase().includes(searchLower) ||
        project.client?.companyName?.toLowerCase().includes(searchLower)
      );
    }

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(project => 
        new Date(project.startDate) >= new Date(filters.dateFrom)
      );
    }
    if (filters.dateTo) {
      filtered = filtered.filter(project => 
        new Date(project.startDate) <= new Date(filters.dateTo)
      );
    }

    // Updates count filter
    if (filters.minUpdates) {
      filtered = filtered.filter(project => 
        (project.projectUpdate?.length || 0) >= parseInt(filters.minUpdates)
      );
    }
    if (filters.maxUpdates) {
      filtered = filtered.filter(project => 
        (project.projectUpdate?.length || 0) <= parseInt(filters.maxUpdates)
      );
    }

    // Tickets count filter
    if (filters.minTickets) {
      filtered = filtered.filter(project => 
        (project.ticket?.length || 0) >= parseInt(filters.minTickets)
      );
    }
    if (filters.maxTickets) {
      filtered = filtered.filter(project => 
        (project.ticket?.length || 0) <= parseInt(filters.maxTickets)
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'client') {
          aValue = a.client?.companyName || '';
          bValue = b.client?.companyName || '';
        } else if (sortConfig.key === 'startDate') {
          aValue = new Date(a.startDate);
          bValue = new Date(b.startDate);
        } else if (sortConfig.key === 'updates') {
          aValue = a.projectUpdate?.length || 0;
          bValue = b.projectUpdate?.length || 0;
        } else if (sortConfig.key === 'tickets') {
          aValue = a.ticket?.length || 0;
          bValue = b.ticket?.length || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredProjects(filtered);
  }, [projects, filters, sortConfig]);

  // Get unique clients for filter dropdown
const uniqueClients = projects ? 
  projects
    .map(project => project.client)
    .filter(Boolean)
    .reduce((acc, client) => {
      if (!acc.find(c => c._id === client._id)) {
        acc.push(client);
      }
      return acc;
    }, []) : [];


  // Calculate summary statistics
  const summaryStats = {
    totalProjects: filteredProjects.length,
    activeProjects: filteredProjects.filter(p => p.status === 'ACTIVE').length,
    completedProjects: filteredProjects.filter(p => p.status === 'COMPLETED').length,
    totalUpdates: filteredProjects.reduce((sum, p) => sum + (p.projectUpdate?.length || 0), 0),
    totalTickets: filteredProjects.reduce((sum, p) => sum + (p.ticket?.length || 0), 0),
  };

  // Fetch projects on component mount
  useEffect(() => {
    if (!projects.length) {
      getProjects();
    }
  }, [getProjects, projects]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: "ALL",
      client: "ALL",
      searchTerm: "",
      dateFrom: "",
      dateTo: "",
      minUpdates: "",
      maxUpdates: "",
      minTickets: "",
      maxTickets: "",
    });
    setSortConfig({ key: null, direction: 'asc' });
  };

  // Export functionality (placeholder)
  const handleExport = () => {
    console.log("Exporting projects data:", filteredProjects);
    alert("Export functionality would be implemented here");
  };

  // Handle delete confirmation
  const confirmDelete = (projectId) => {
    setProjectToDelete(projectId);
    setShowConfirmModal(true);
  };

  // Handle project deletion
  const handleDeleteProject = async () => {
    try {
      await ProjectService.deleteProject(projectToDelete);
      setProjects(
        projects.filter((project) => project._id !== projectToDelete)
      );
      setShowConfirmModal(false);
      setProjectToDelete(null);
    } catch (err) {
      setError("Failed to delete project. Please try again.");
      console.error("Error deleting project:", err);
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800 border-green-200";
      case "COMPLETED":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "ON_HOLD":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "CANCELLED":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Render confirmation modal
  const ConfirmDeleteModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
        <p>
          Are you sure you want to delete this project? This action cannot be
          undone.
        </p>
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={() => setShowConfirmModal(false)}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteProject}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/dashboard")}
            className="mr-4 text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex items-center space-x-2">
            <FileText className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl font-semibold text-gray-800">
              Project Management
            </h1>
            {projectLoading && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => getProjects()}
            disabled={projectLoading}
            className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-1 disabled:opacity-50"
          >
            <RefreshCw size={16} className={projectLoading ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>
          <button
            onClick={resetFilters}
            className="px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-1"
          >
            <Filter size={16} />
            <span>Reset Filters</span>
          </button>
          <button 
            onClick={handleExport}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-1"
          >
            <Download size={16} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6"
          role="alert"
        >
          <p>{error}</p>
        </div>
      )}

      {/* Filters and Summary Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg p-3 border">
              <div className="flex items-center space-x-2">
                <FileText size={16} className="text-blue-600" />
                <div>
                  <p className="text-xs font-medium text-gray-500">Total Projects</p>
                  <p className="text-lg font-bold text-gray-900">{summaryStats.totalProjects}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <div className="flex items-center space-x-2">
                <Activity size={16} className="text-green-600" />
                <div>
                  <p className="text-xs font-medium text-gray-500">Active</p>
                  <p className="text-lg font-bold text-green-600">{summaryStats.activeProjects}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Completed</p>
                  <p className="text-lg font-bold text-blue-600">{summaryStats.completedProjects}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <div className="flex items-center space-x-2">
                <Edit size={16} className="text-purple-600" />
                <div>
                  <p className="text-xs font-medium text-gray-500">Total Updates</p>
                  <p className="text-lg font-bold text-purple-600">{summaryStats.totalUpdates}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Total Tickets</p>
                  <p className="text-lg font-bold text-yellow-600">{summaryStats.totalTickets}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="CANCELLED">Cancelled</option>
            </select>

            {/* Client Filter */}
            {user.role !== "CLIENT" && (
              <select
                value={filters.client}
                onChange={(e) => handleFilterChange('client', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="ALL">All Clients</option>
                {uniqueClients.map(client => (
                  <option key={client._id} value={client._id}>
                    {client.companyName}
                  </option>
                ))}
              </select>
            )}

            {/* Date From */}
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                placeholder="Start Date From"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                placeholder="Start Date To"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Loading spinner */}
        {projectLoading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading projects...</span>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="p-8 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <div className="text-gray-500 text-lg">No projects found</div>
            <div className="text-gray-400 text-sm">
              {projects.length === 0 ? "No projects available" : "Try adjusting your filters"}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Project Name</span>
                      {sortConfig.key === 'name' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  {user.role !== "CLIENT" && (
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('client')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Company Name</span>
                        {sortConfig.key === 'client' && (
                          <span className="text-blue-600">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  )}
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('updates')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Updates</span>
                      {sortConfig.key === 'updates' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('tickets')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Tickets</span>
                      {sortConfig.key === 'tickets' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('startDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Start Date</span>
                      {sortConfig.key === 'startDate' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortConfig.key === 'status' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProjects.map((project) => (
                  <tr key={project._id} className="hover:bg-gray-50 transition-colors">
                    {/* Project Name */}
                    <td className="px-6 py-4">
                      <Link
                        to={`/dashboard/projects/${project._id}/details`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {project.name}
                          </p>
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {project.description
                              ? `${project.description.slice(0, 60)}${
                                  project.description.length > 60 ? "..." : ""
                                }`
                              : "No description provided."}
                          </p>
                        </div>
                      </Link>
                    </td>

                    {/* Company Name */}
                    {user.role !== "CLIENT" && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Building size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-900">
                            <Link to=""> {project.client?.companyName || "N/A"}</Link>
                          </span>
                        </div>
                      </td>
                    )}

                    {/* Updates */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold">
                        {project.projectUpdate?.length || 0}
                      </span>
                    </td>

                    {/* Tickets */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                        {project.ticket?.length || 0}
                      </span>
                    </td>

                    {/* Start Date */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(project.startDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 text-xs rounded-full font-semibold border ${getStatusBadgeClass(
                          project.status
                        )}`}
                      >
                        {project.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <Link
                          to={`/dashboard/projects/${project._id}`}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="View Project"
                        >
                          <FaEye className="text-lg" />
                        </Link>
                        <Link
                          to={`/dashboard/projects/${project._id}/edit`}
                          className="text-yellow-600 hover:text-yellow-900 transition-colors"
                          title="Edit Project"
                        >
                          <FaEdit className="text-lg" />
                        </Link>
                        <button
                          onClick={() => confirmDelete(project._id)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Delete Project"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Table Footer */}
        {filteredProjects.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-2 md:space-y-0">
              <div className="text-sm text-gray-600">
                Showing {filteredProjects.length} of {projects.length} projects
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Active:</span>
                  <span className="text-green-600">{summaryStats.activeProjects}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Completed:</span>
                  <span className="text-blue-600">{summaryStats.completedProjects}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Updates:</span>
                  <span className="text-purple-600">{summaryStats.totalUpdates}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Tickets:</span>
                  <span className="text-yellow-600">{summaryStats.totalTickets}</span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && <ConfirmDeleteModal />}
    </div>
  );
};

export default Projects;